import { PaymentMethod } from '@daymade/common-lib/dbEnums';
import { ZEntryLine, ZPromoConditions } from '@daymade/common-lib/dbJsonModels';
import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  ZDrawScheduleSelect,
  ZDrawTypePlanSelect,
  ZDrawTypeSelect,
} from '../draw/draw';
import { ZDrawOrderSelect } from '../draw/drawOrder/models';
import { ZOrderSelect } from '../order/models';
import { ZPromoCodeRecurringSelect, ZPromoCodeSelect } from '../promo/models';
import { successSchema } from '../utils';
import { ZSubscriptionHistorySelect, ZSubscriptionSelect } from './models';

const c = initContract();
export const authSubscriptionContract = c.router(
  {
    createSubscription: {
      method: 'POST',
      path: '/',
      responses: {
        200: z.object({
          orderId: z.string(),
          adyenPaymentSession: z
            .object({
              sessionData: z.string(),
              id: z.string(),
              adyenPaymentSessionId: z.string(),
            })
            .optional(),
          checkoutComPaymentSession: z
            .object({
              id: z.string(),
              paymentSessionResponse: z.record(z.string(), z.unknown()),
              checkoutComPaymentSessionId: z.string(),
            })
            .optional(),
        }),
        400: z.object({
          message: z.string(),
          reason: z
            .union([
              z.literal('WOULD_EXCEED_SPEND_LIMIT'),
              z.literal('HAS_ACTIVE_SUBSCRIPTION'),
              z.literal('ACCOUNT_SUSPENDED'),
              z.literal('REFERRAL_PLAN_CODE_NOT_MET'),
            ])
            .optional(),
          spendLimitPence: z.number().optional(),
        }),
      },
      body: z.object({
        drawTypeCode: z.string(),
        drawSchedulePlans: z
          .object({
            drawScheduleId: z.number(),
            drawTypePlanCode: z.string(),
            drawTypePlanSubcode: z.string(),
          })
          .array(),
        entries: z.array(ZEntryLine),
        expectedCost: z.number(),
        promoCode: z.optional(z.string()),
        isUpgrade: z.optional(z.boolean()).default(false),
        referralId: z.optional(z.string()),
        utmParams: z.optional(z.record(z.string(), z.string())).nullable(),
        attributionParams: z
          .optional(z.record(z.string(), z.string()))
          .nullable(),
        paymentMethod: z.nativeEnum(PaymentMethod).optional(),
        sourceFor3DS: z.string(),
        evalData: ZPromoConditions.partial().optional(),
      }),
    },

    updateSubscription: {
      method: 'PATCH',
      path: '/:subscription_id',
      body: z.object({
        drawSchedulePlans: z
          .object({
            drawScheduleId: z.number(),
            drawTypePlanCode: z.string(),
            drawTypePlanSubcode: z.string(),
          })
          .array()
          .optional(),
        entries: z.array(ZEntryLine).optional(),
        applyDoubleChancesPromo: z.boolean().optional(),
        expectedCost: z.number(),
        version: z.number(),
      }),
      responses: {
        200: successSchema,
        400: z.object({
          message: z.string(),
          reason: z.literal('WOULD_EXCEED_SPEND_LIMIT').optional(),
          spendLimitPence: z.number().optional(),
        }),
      },
    },

    cancelSubscription: {
      method: 'POST',
      path: '/:subscription_id/cancel',
      pathParams: z.object({
        subscription_id: z.string(),
      }),
      body: z.object({
        cancellationReason: z.string().optional(),
      }),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(), // player not found or no subscription
      },
    },

    snoozeSubscription: {
      method: 'POST',
      path: '/:subscription_id/snooze',
      pathParams: z.object({
        subscription_id: z.string(),
      }),
      body: z.object({
        snoozeDuration: z.number(),
        snoozeUnits: z.string(),
      }),
      responses: {
        200: z.object({
          snoozedUntil: z.coerce.date(),
        }),
        400: c.type<{ message: string }>(),
      },
    },

    resumeSubscription: {
      method: 'POST',
      path: '/:subscription_id/resume',
      pathParams: z.object({
        subscription_id: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: successSchema,
        400: c.type<{ message: string }>(),
      },
    },

    getSubscriptionHistory: {
      method: 'GET',
      path: '/:subscription_id/history',
      pathParams: z.object({
        subscription_id: z.string(),
      }),
      responses: {
        200: ZSubscriptionHistorySelect.array(),
        404: c.type<{ message: string }>(),
      },
    },

    createChangePaymentMethodSession: {
      method: 'POST',
      path: '/:subscription_id/change-payment-method',
      pathParams: z.object({
        subscription_id: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: z.object({
          adyenPaymentSession: z
            .object({
              sessionData: z.string(),
              id: z.string(),
              adyenPaymentSessionId: z.string(),
            })
            .optional(),
          checkoutComPaymentSession: z
            .object({
              id: z.string(),
              paymentSessionResponse: z.record(z.string(), z.unknown()),
              checkoutComPaymentSessionId: z.string(),
            })
            .optional(),
          willBeChargedImmediately: z.boolean(),
        }),
        400: c.type<{ message: string }>(),
      },
    },

    getPlayerSubscriptions: {
      method: 'GET',
      path: '/player',
      responses: {
        200: z.array(
          z.object({
            order: ZOrderSelect,
            drawOrder: ZDrawOrderSelect,
            subscription: ZSubscriptionSelect,
            drawSchedulePlans: z
              .object({
                drawSchedule: ZDrawScheduleSelect,
                drawTypePlan: ZDrawTypePlanSelect,
              })
              .array(),
            drawType: ZDrawTypeSelect,
            promoCodeRecurring: z.optional(ZPromoCodeRecurringSelect),
            promoCode: z.optional(ZPromoCodeSelect),
          }),
        ),
      },
    },

    resendConfirmationEmail: {
      method: 'POST',
      path: '/:subscription_id/resend-confirmation-email',
      pathParams: z.object({
        subscription_id: z.string(),
      }),
      body: z.object({}),
      responses: {
        200: successSchema,
        400: z.object({
          message: z.string(),
          reason: z
            .union([
              z.literal('SUBSCRIPTION_NOT_FOUND'),
              z.literal('NON_OWNED_SUBSCRIPTION'),
            ])
            .optional(),
        }),
      },
    },
  },
  {
    pathPrefix: '/auth',
    strictStatusCodes: true,
    commonResponses: {
      401: c.type<{ message: string; statusCode: 401 }>(),
      403: c.type<{ message: string; statusCode: 403 }>(),
    },
  },
);
